import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../../api/core'
import { useStore } from '../../store'
import type { DocType, FormState } from '../../types/testdata.types'
import { buildDocNumber } from './constants'
import { nextDocumentNumberMode, isAutoManaged } from './docNumberMode'
import type { DocumentNumberMode } from './docNumberMode'

export type { DocumentNumberMode }

/** Dagsleutel (lokale kalenderdag) waarop de server-teller resettet
 * (`doc_counters.py`/`persistence.py` resetten op `date.today()`). Gebruikt
 * om de client-cache hieronder ongeldig te maken zodra de dag omslaat. */
function todayKey(): string {
  return new Date().toDateString()
}

/** Houdt het documentnummer in sync met het per-gebruiker/per-doctype teller-
 * endpoint (`/api/doc-counters/:docType`) en bewaakt het onderscheid tussen
 * AUTO-MANAGED (systeem-voorgesteld, mag hersynct worden) en USER-MANAGED
 * (handmatig gewijzigd, wordt nooit meer stil overschreven) — zie
 * `XMLInsights-SDD` `REQ-TESTDATA-001`.
 *
 * De mode wordt expliciet bijgehouden (nooit via patroon/regex op de waarde
 * afgeleid — een handmatig getypte waarde die toevallig op het auto-formaat
 * lijkt, blijft user-managed). `documentNumberModeRef` is de bron van
 * waarheid binnen async callbacks/effects (voorkomt stale-closure-reads);
 * `documentNumberMode` (state) bestaat ernaast puur om de UI er ooit op te
 * kunnen laten reageren.
 *
 * De cache (`counterLoadedForType`/`counterLoadedDate`) wordt uitsluitend bij
 * een geslaagde fetch gezet — anders bleef een mislukte eerste fetch de cache
 * permanent "geladen" markeren — en wordt ongeldig zodra de kalenderdag
 * omslaat, want de server reset de teller per dag (PR #44). Die cache-fix
 * alleen is niet genoeg om een lang openstaande tab correct te houden (de
 * mount-effect reruns niet vanzelf enkel omdat de tijd verstrijkt) — vandaar
 * `ensureCurrentAutoDocumentNumber` hieronder, dat vlak vóór elke submit een
 * verse server-peek doet voor auto-managed velden. */
export function useDocNumber(
  docType: DocType,
  initials: string,
  setForm: React.Dispatch<React.SetStateAction<FormState>>,
) {
  const docNumCounterRef = useRef(1)
  const counterLoadedForType = useRef<string | null>(null)
  const counterLoadedDate = useRef<string | null>(null)
  const prevDocTypeRef = useRef<DocType | null>(null)

  const [documentNumberMode, setDocumentNumberModeState] = useState<DocumentNumberMode>('auto')
  const documentNumberModeRef = useRef<DocumentNumberMode>('auto')

  const setDocumentNumberMode = (mode: DocumentNumberMode) => {
    documentNumberModeRef.current = mode
    setDocumentNumberModeState(mode)
  }

  /** Expliciete transitie AUTO -> USER-MANAGED. Aanroepen vanuit de onChange
   * van het documentnummer-veld zelf — nooit afgeleid uit de waarde. */
  const markDocumentNumberManual = () => setDocumentNumberMode('manual')

  useEffect(() => {
    const today = todayKey()
    // Transitieregel (REQ-TESTDATA-001, zie docNumberMode.ts): alleen een
    // echte docType-wissel reset user-managed terug naar auto-managed — niet
    // een dagwisseling of initials-update bij ongewijzigde docType.
    const docTypeChanged = prevDocTypeRef.current !== null && prevDocTypeRef.current !== docType
    const nextMode = nextDocumentNumberMode(prevDocTypeRef.current, docType, documentNumberModeRef.current)
    prevDocTypeRef.current = docType
    if (nextMode !== documentNumberModeRef.current) setDocumentNumberMode(nextMode)

    const cacheValid = counterLoadedForType.current === docType && counterLoadedDate.current === today
    if (cacheValid && !docTypeChanged) {
      if (isAutoManaged(documentNumberModeRef.current)) {
        setForm(f => ({ ...f, document_number: buildDocNumber(docType, initials, docNumCounterRef.current) }))
      }
      return
    }

    apiFetch<{ counter: number }>(`/api/doc-counters/${docType}`)
      .then(data => {
        counterLoadedForType.current = docType
        counterLoadedDate.current = today
        docNumCounterRef.current = data.counter
        // Lees initialen op het moment van resolve — voorkomt race met getMe()
        const latestInitials = useStore.getState().currentUser?.initials ?? ''
        if (isAutoManaged(documentNumberModeRef.current)) {
          setForm(f => ({ ...f, document_number: buildDocNumber(docType, latestInitials, data.counter) }))
        }
      })
      .catch(() => {
        const latestInitials = useStore.getState().currentUser?.initials ?? ''
        if (isAutoManaged(documentNumberModeRef.current)) {
          setForm(f => ({ ...f, document_number: buildDocNumber(docType, latestInitials, docNumCounterRef.current) }))
        }
      })
  }, [docType, initials])

  /** Ná een geslaagde generatie/verzending: haal het volgende voorgestelde
   * nummer op. Het nieuw voorgestelde nummer is per definitie weer
   * auto-managed (REQ-TESTDATA-001 "succesvolle generatie -> AUTO-MANAGED
   * voor het nieuwe voorgestelde nummer") — ook wanneer het vorige nummer
   * handmatig was overschreven, want dit betreft een nieuw, vers voorstel. */
  const refreshDocCounter = () => {
    counterLoadedForType.current = null
    counterLoadedDate.current = null
    setDocumentNumberMode('auto')
    apiFetch<{ counter: number }>(`/api/doc-counters/${docType}`)
      .then(data => {
        counterLoadedForType.current = docType
        counterLoadedDate.current = todayKey()
        docNumCounterRef.current = data.counter
        const latestInitials = useStore.getState().currentUser?.initials ?? ''
        setForm(f => ({ ...f, document_number: buildDocNumber(docType, latestInitials, data.counter) }))
      })
      .catch(() => {
        docNumCounterRef.current += 1
        const latestInitials = useStore.getState().currentUser?.initials ?? ''
        setForm(f => ({ ...f, document_number: buildDocNumber(docType, latestInitials, docNumCounterRef.current) }))
      })
  }

  /** Gedeelde pre-submit sync — de enige plek die bepaalt welk documentnummer
   * daadwerkelijk in een request terechtkomt. Wordt vlak vóór het bouwen van
   * de request-payload aangeroepen door elk submit-pad (generate, send-email,
   * generate-set, debet/credit generate/email).
   *
   * - USER-MANAGED: geen server-fetch, geen overschrijving — geeft
   *   `currentVisibleValue` ongewijzigd terug (de aanroeper geeft de waarde
   *   mee zoals die op het moment van klikken zichtbaar was; nooit een
   *   her-lezing van form-state ná een async gat).
   * - AUTO-MANAGED: haalt de actuele server-suggestie op, werkt het
   *   formulier bij indien de waarde is gewijzigd, en geeft ALTIJD de vers
   *   opgehaalde waarde terug. De aanroeper MOET dit retourtype rechtstreeks
   *   in de request-payload gebruiken — niet `form.document_number` na de
   *   `await`, want een React state-update uit deze functie is niet
   *   gegarandeerd zichtbaar binnen dezelfde aanroepende functie-uitvoering.
   * - Gooit door bij een fetch-fout (auto-managed only): de aanroeper vangt
   *   dit af in zijn bestaande try/catch en toont de bestaande foutafhandeling
   *   in plaats van stilzwijgend met een mogelijk verouderd nummer door te
   *   gaan (REQ-TESTDATA-001, "Pre-submit sync failure").
   */
  const ensureCurrentAutoDocumentNumber = async (currentVisibleValue: string): Promise<string> => {
    if (!isAutoManaged(documentNumberModeRef.current)) {
      return currentVisibleValue
    }
    const data = await apiFetch<{ counter: number }>(`/api/doc-counters/${docType}`)
    counterLoadedForType.current = docType
    counterLoadedDate.current = todayKey()
    docNumCounterRef.current = data.counter
    const latestInitials = useStore.getState().currentUser?.initials ?? ''
    const fresh = buildDocNumber(docType, latestInitials, data.counter)
    setForm(f => (f.document_number === fresh ? f : { ...f, document_number: fresh }))
    return fresh
  }

  return {
    docNumCounterRef,
    refreshDocCounter,
    documentNumberMode,
    markDocumentNumberManual,
    ensureCurrentAutoDocumentNumber,
  }
}
